import { ReactNode, useState } from 'react';
import { ActiveDownload, useDownloadLifecycle } from './useDownloadLifecycle';
import { handleQueryError, isErrorResponse } from '../../notifications/ErrorNotification';
import { ExportTooLargeProblemCodec } from '../../api/types/problemApi.types';
import { showSuccessNotification } from '../../notifications/SuccessNotification';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { FormattedMessage } from 'react-intl';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { TooLargeErrorAlert } from './TooLargeErrorAlert';
import { ExportModalFooter } from './ExportModalFooter';

export const EXPORT_BUTTON = 'export-button';

type ExportModalProps = {
    tooltip: string;
    modalTitle: string;
    filename: string;
    getExport: () => ActiveDownload;
    body: ReactNode;
    resetFilters: () => void;
    isDisabled: boolean;
};

export const ExportModal = (props: ExportModalProps) => {
    const [showExportModal, setShowExportModal] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);

    const { downloadIsActive, startDownload, cancelDownload } = useDownloadLifecycle({
        initDownload: props.getExport,
        onError: (error) => {
            if (isErrorResponse(error, ExportTooLargeProblemCodec)) {
                setShowErrorAlert(true);
            } else {
                handleQueryError(error);
            }
        },
        onDownloadFinished: () => {
            showSuccessNotification('outboundOrderBook.notification.download.success');
            setShowErrorAlert(false);
            setShowExportModal(false);
        },
        fallbackFilename: props.filename,
    });

    const openModal = () => setShowExportModal(true);

    const handleCancel = () => {
        cancelDownload();
        setShowErrorAlert(false);
        setShowExportModal(false);
    };

    return (
        <>
            <OverlayTrigger
                placement="bottom-end"
                overlay={
                    <Tooltip id="tooltip" allowOnTouch>
                        <FormattedMessage id={props.tooltip} />
                    </Tooltip>
                }
            >
                <button
                    type="button"
                    className="btn btn-default btn-icon-only"
                    onClick={openModal}
                    data-testid={EXPORT_BUTTON}
                >
                    <span className="rioglyph rioglyph-download" aria-hidden="true" />
                </button>
            </OverlayTrigger>

            <Dialog
                show={showExportModal}
                bsSize={Dialog.SIZE_MD}
                title={<FormattedMessage id={props.modalTitle} />}
                body={
                    <>
                        {showErrorAlert ? <TooLargeErrorAlert hideAlert={() => setShowErrorAlert(false)} /> : undefined}
                        <div className="margin-bottom-20">
                            <div className="margin-bottom-10">
                                <span>
                                    <FormattedMessage id="outboundOrderBook.export.description" />
                                </span>
                            </div>
                            {props.body}
                        </div>
                    </>
                }
                footer={
                    <ExportModalFooter
                        handleCancel={handleCancel}
                        handleDownload={startDownload}
                        isLoading={downloadIsActive}
                        downloadDisabled={props.isDisabled}
                        resetFilters={props.resetFilters}
                    />
                }
                onClose={handleCancel}
            />
        </>
    );
};
